import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Download, Loader2, FileText, Eye, Pencil,
} from "lucide-react";
import { toast } from "../../utils/toast";
import api from "../../api/axios";
import { LETTER_TYPES } from "../../utils/letterTemplates";
import {
  T, Card, PrimaryButton, SecondaryButton, Label, Input, StatusBadge,
} from "../../components/em/ui";

/**
 * Dedicated full-page view/editor for a single generated letter.
 * Left: a live A4 "paper" rendering. Right: edit fields (subject, body,
 * signatory). Save updates the letter in place and regenerates the PDF.
 */
const LetterView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const basePath = role === "hr" ? "/hr" : "/admin";
  const companyData = useMemo(
    () => JSON.parse(localStorage.getItem("companyData") || "null"),
    [],
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [mode, setMode] = useState("view"); // 'view' | 'edit'

  const [letter, setLetter] = useState(null);
  // Editable fields
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryTitle, setSignatoryTitle] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/settings/generated-letters/${id}`);
        if (!alive) return;
        const pd =
          data.placeholder_data && typeof data.placeholder_data === "string"
            ? safeParse(data.placeholder_data)
            : data.placeholder_data || {};
        setLetter({ ...data, placeholder_data: pd });
        setSubject(data.subject || "");
        setContent(data.content || "");
        setSignatoryName(pd.signatoryName || pd.signatory_name || "");
        setSignatoryTitle(pd.signatoryTitle || pd.signatory_title || "");
      } catch (err) {
        console.error("Error loading letter:", err);
        toast.error("Could not load this letter");
        navigate(`${basePath}/generateletter`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const safeParse = (s) => { try { return JSON.parse(s); } catch { return {}; } };

  const typeLabel =
    LETTER_TYPES.find((t) => t.value === letter?.template_type)?.label ||
    letter?.template_type ||
    "Letter";

  const dirty =
    letter &&
    (subject !== (letter.subject || "") ||
      content !== (letter.content || "") ||
      signatoryName !== (letter.placeholder_data?.signatoryName || letter.placeholder_data?.signatory_name || "") ||
      signatoryTitle !== (letter.placeholder_data?.signatoryTitle || letter.placeholder_data?.signatory_title || ""));

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const refId = letter?.id ? String(letter.id).padStart(6, "0") : "—";

  const handleSave = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error("Subject and body can't be empty");
      return;
    }
    setSaving(true);
    try {
      const placeholder_data = {
        ...(letter.placeholder_data || {}),
        signatoryName,
        signatoryTitle,
      };
      const { data } = await api.put(`/settings/generated-letters/${id}`, {
        subject,
        content,
        placeholder_data,
      });
      const pd =
        data.placeholder_data && typeof data.placeholder_data === "string"
          ? safeParse(data.placeholder_data)
          : data.placeholder_data || placeholder_data;
      setLetter({ ...data, placeholder_data: pd });
      toast.success("Letter saved");
      setMode("view");
    } catch (err) {
      console.error("Error saving letter:", err);
      toast.error(err?.response?.data?.error || "Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/settings/generated-letters/${id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${typeLabel}.pdf`.replace(/[/\\]+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading letter:", err);
      toast.error(
        err?.response?.status === 404
          ? "No PDF available yet — save the letter to regenerate it"
          : "Failed to download letter",
      );
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "60px 28px", textAlign: "center", color: T.fg5, font: `400 14px ${T.font}` }}>
        Loading letter…
      </div>
    );
  }

  const editing = mode === "edit";

  return (
    <div style={{ padding: "22px 28px 48px", maxWidth: 1280, margin: "0 auto" }}>
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(`${basePath}/generateletter`)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: 0, cursor: "pointer", color: T.fg5, font: `500 13px ${T.font}`, padding: 0, marginBottom: 14 }}
      >
        <ArrowLeft size={15} /> Back to letters
      </button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FileText size={22} color={T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ font: `700 22px ${T.font}`, color: T.fg1, letterSpacing: "-0.02em" }}>{typeLabel}</span>
            <StatusBadge label={letter?.status || "generated"} />
          </div>
          <div style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>
            Ref LTR-{refId} · {formatDate(letter?.created_at)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {editing ? (
            <>
              <SecondaryButton
                icon={Eye}
                onClick={() => {
                  // Discard edits and return to view
                  setSubject(letter.subject || "");
                  setContent(letter.content || "");
                  setSignatoryName(letter.placeholder_data?.signatoryName || letter.placeholder_data?.signatory_name || "");
                  setSignatoryTitle(letter.placeholder_data?.signatoryTitle || letter.placeholder_data?.signatory_title || "");
                  setMode("view");
                }}
                disabled={saving}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton icon={saving ? Loader2 : Save} onClick={handleSave} disabled={saving || !dirty}>
                {saving ? "Saving…" : "Save changes"}
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                icon={downloading ? Loader2 : Download}
                onClick={handleDownload}
                disabled={downloading}
              >
                Download PDF
              </SecondaryButton>
              <PrimaryButton icon={Pencil} onClick={() => setMode("edit")}>
                Edit letter
              </PrimaryButton>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ── A4 paper (live) ── */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Card padding={0} style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${T.divider}`, background: T.bgHead }}>
              <span style={{ font: `700 13px ${T.font}`, color: T.fg1 }}>{editing ? "Live Preview" : "Letter"}</span>
            </div>
            <div style={{ padding: 28, background: T.bgHead, display: "flex", justifyContent: "center" }}>
              <div style={{ width: "100%", maxWidth: 660, background: "#fff", borderRadius: 8, boxShadow: T.shadowMd, padding: "44px 48px", color: "#1f2937" }}>
                {/* Company header */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 20, marginBottom: 14 }}>
                  <div style={{ minWidth: 0 }}>
                    {(companyData?.logoUrl) && (
                      <img src={companyData.logoUrl} alt="Logo" crossOrigin="anonymous" style={{ maxHeight: 46, marginBottom: 8 }} />
                    )}
                    <div style={{ font: `700 17px ${T.font}`, color: "#0a0a0a" }}>{companyData?.name || "Company Name"}</div>
                    <div style={{ font: `400 11.5px/1.6 ${T.font}`, color: "#6b7280", marginTop: 3 }}>
                      {companyData?.address && <div>{companyData.address}</div>}
                      {companyData?.country && <div>{companyData.country}</div>}
                      {companyData?.websiteUrl && <div>{companyData.websiteUrl}</div>}
                      {companyData?.email && <div>Email: {companyData.email}</div>}
                      {companyData?.phone && <div>Phone: {companyData.phone}</div>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", font: `400 11.5px/1.6 ${T.font}`, color: "#6b7280", whiteSpace: "nowrap" }}>
                    <div>Date: {formatDate(letter?.created_at)}</div>
                    <div>Ref: LTR-{refId}</div>
                  </div>
                </div>

                <hr style={{ border: 0, borderTop: "1px solid #e5e7eb", margin: "0 0 18px" }} />

                {/* Subject */}
                <div style={{ font: `400 13.5px ${T.font}`, color: "#111827", marginBottom: 16 }}>
                  <strong>Subject:</strong> {subject || <span style={{ color: "#9ca3af" }}>No subject</span>}
                </div>

                {/* Body */}
                <div style={{ font: `400 13px/1.85 ${T.font}`, color: "#1f2937", whiteSpace: "pre-wrap" }}>
                  {content || <span style={{ color: "#9ca3af" }}>No content.</span>}
                </div>

                {/* Signature */}
                <div style={{ marginTop: 48, font: `400 13px/1.6 ${T.font}`, color: "#111827" }}>
                  {companyData?.signatureUrl && (
                    <img src={companyData.signatureUrl} alt="Signature" crossOrigin="anonymous" style={{ maxHeight: 52, display: "block", marginBottom: 6 }} />
                  )}
                  <div style={{ fontWeight: 600 }}>{signatoryName || "Authorised Signatory"}</div>
                  <div style={{ color: "#6b7280" }}>{signatoryTitle || "HR Department"}</div>
                  <div style={{ color: "#6b7280" }}>{companyData?.name || "Company Name"}</div>
                </div>

                {/* Footer */}
                <div style={{ marginTop: 36, paddingTop: 12, borderTop: "1px solid #e5e7eb", textAlign: "center", font: `400 10.5px/1.6 ${T.font}`, color: "#9ca3af" }}>
                  <div>This is a computer-generated letter and does not require a physical signature.</div>
                  {companyData?.gstNo && <div>GST No: {companyData.gstNo}</div>}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* ── Edit form (only in edit mode) ── */}
        {editing && (
          <div style={{ width: 380, flexShrink: 0, minWidth: 300 }}>
            <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ font: `700 12px ${T.font}`, color: T.fg1, textTransform: "uppercase", letterSpacing: "0.04em" }}>Edit letter</div>
              <div>
                <Label>Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line…" />
              </div>
              <div>
                <Label>Body</Label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={16}
                  placeholder="Letter body…"
                  style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: T.rMd, background: "var(--em-bg-card)", font: `400 13px/1.7 ${T.font}`, color: T.fg1, outline: "none", boxSizing: "border-box", resize: "vertical" }}
                />
              </div>
              <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ font: `600 11px ${T.font}`, color: T.fg5, letterSpacing: "0.03em", textTransform: "uppercase" }}>Signatory</div>
                <div>
                  <Label>Name</Label>
                  <Input value={signatoryName} onChange={(e) => setSignatoryName(e.target.value)} placeholder="Authorised Signatory" />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={signatoryTitle} onChange={(e) => setSignatoryTitle(e.target.value)} placeholder="HR Department" />
                </div>
              </div>
              <PrimaryButton
                icon={saving ? Loader2 : Save}
                onClick={handleSave}
                disabled={saving || !dirty}
                style={{ justifyContent: "center" }}
              >
                {saving ? "Saving…" : "Save changes"}
              </PrimaryButton>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default LetterView;
